import React, { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import styles from './AccountDetailsPage.module.css';
import {
  FaUser,
  FaEnvelope,
  FaCreditCard,
  FaCalendarAlt,
  FaShieldAlt,
  FaUniversity,
  FaMobileAlt,
  FaMapMarkerAlt,
  FaIdCard,
} from 'react-icons/fa';
import MessageBox from '../../components/MessageBox';

function AccountDetailsPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await api.getUser();
        setUser(data);
        setLoading(false);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to fetch user data.' });
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleCloseMessage = () => {
    setMessage(null);
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (!user) return <div className={styles.error}>Error loading user data.</div>;

  const getKycStatus = () => {
    if (user.aadhaarNumber && user.panNumber && user.dateOfBirth) {
      return <span className={`${styles['kyc-status']} ${styles.completed}`}>Verified</span>;
    }
    return <span className={`${styles['kyc-status']} ${styles['not-started']}`}>Unverified</span>;
  };

  return (
    <div className={styles['account-details-page']}>
      {message && <MessageBox message={message.text} type={message.type} onClose={handleCloseMessage} />}
      
      <div className={styles['page-header']}>
        <h1>Account Details</h1>
        <p>View and manage your personal, identity, and banking information.</p>
      </div>

      <div className={styles['details-grid']}>
        {/* Personal Details */}
        <div className={styles['details-section']}>
          <div className={styles['section-header']}>
            <FaUser className={styles['section-icon']} />
            <h3>Personal Information</h3>
          </div>
          <div className={styles['section-content']}>
            <DetailItem icon={<FaUser />} label="Full Name" value={user.name} />
            <DetailItem icon={<FaEnvelope />} label="Email Address" value={user.email} />
            <DetailItem icon={<FaMobileAlt />} label="Mobile Number" value={user.mobileNumber || 'Not Provided'} />
            <DetailItem icon={<FaCalendarAlt />} label="Date of Birth" value={user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Not Provided'} />
          </div>
        </div>

        {/* Identity & KYC */}
        <div className={styles['details-section']}>
          <div className={styles['section-header']}>
            <FaShieldAlt className={styles['section-icon']} />
            <h3>Identity & KYC</h3>
          </div>
          <div className={styles['section-content']}>
            <DetailItem icon={<FaIdCard />} label="Aadhaar Card" value={user.aadhaarNumber ? `XXXX-XXXX-${user.aadhaarNumber.slice(-4)}` : 'Not Provided'} />
            <DetailItem icon={<FaCreditCard />} label="PAN Card" value={user.panNumber || 'Not Provided'} />
            <DetailItem icon={<FaShieldAlt />} label="Verification Status" value={getKycStatus()} isRaw />
          </div>
        </div>

        {/* Bank Details */}
        <div className={styles['details-section']}>
          <div className={styles['section-header']}>
            <FaUniversity className={styles['section-icon']} />
            <h3>Banking Details</h3>
          </div>
          <div className={styles['section-content']}>
            <DetailItem icon={<FaUniversity />} label="Bank Name" value={user.bankName || 'Not Provided'} />
            <DetailItem icon={<FaIdCard />} label="Account Number" value={user.accountNumber || 'Not Provided'} />
            <DetailItem icon={<FaMapMarkerAlt />} label="IFSC Code" value={user.ifscCode || 'Not Provided'} />
          </div>
        </div>
      </div>
    </div>
  );
}

const DetailItem = ({ icon, label, value, isRaw }) => (
  <div className={styles['detail-item']}>
    <div className={styles['detail-label-container']}>
      <span className={styles['detail-icon']}>{icon}</span>
      <span className={styles['detail-label']}>{label}</span>
    </div>
    <div className={styles['detail-value']}>
      {isRaw ? value : <span>{value}</span>}
    </div>
  </div>
);

export default AccountDetailsPage;
